import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import { DataTable } from 'components/tables/data-table';
import { useMemo } from 'react';
import { Control, SetFieldValue } from 'react-hook-form';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { ClassifierData } from 'types/classifiers';
import { CorporateRoleFillingForm, CorporateRoleType } from 'types/companies';
import { getClassifierText, t } from 'utils/getTranslations';
import { CorporateRoleFillingRowType } from './CorporateRolePage';

interface Props {
    isReadOnly: boolean;
    dataToDisplay: CorporateRoleFillingRowType[];
    setValue: SetFieldValue<CorporateRoleFillingForm>;
    control: Control<CorporateRoleFillingForm>;
    classifiers: Record<string, ClassifierData>;
}

const CorporateRoleFillingsTable = ({
    control,
    setValue,
    isReadOnly,
    dataToDisplay,
    classifiers,
}: Props): JSX.Element => {
    const columns = useMemo<DataTableColumnDef<CorporateRoleFillingRowType>[]>(
        () => [
            {
                header: t('corporate-role.level'),
                accessorKey: 'name',
                enableSorting: false,
                meta: {
                    // Measured at 2560: 970 / 624 / 624 px.
                    widthWeight: 1.55,
                },
                cell: ({ cell }) => {
                    const { name, level } = cell.row.original;
                    const levelStep = (level - 1) * 10;
                    return (
                        <div className="flex font-semibold whitespace-nowrap">
                            <div style={{ width: `${levelStep}%` }} />
                            <div style={{ width: `${100 - levelStep}%` }}>{name}</div>
                        </div>
                    );
                },
            },
            {
                header: getClassifierText(
                    classifiers?.['CORPORATE_ROLE_TYPE'],
                    CorporateRoleType.LEADING_REVENUE_PERSON
                ),
                accessorKey: 'leadingRevenuePersonIndex',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    padding: '0px 0px 0px 16px',
                },
                cell: ({ getValue }) => (
                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        gap={0}
                        name={`corporateRoleFillings.${getValue<number | undefined>()!}.roleFilledByEmployee`}
                        paginated={{
                            fetch: useLazyGetEmployeesListWithFiltersQuery,
                            searchField: 'fullName',
                        }}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        readonly={isReadOnly ? { linkTo: 'employee' } : false}
                        noReadOnlyDivider
                    />
                ),
            },
            {
                header: getClassifierText(
                    classifiers?.['CORPORATE_ROLE_TYPE'],
                    CorporateRoleType.LEADING_COMPLIANCE_PERSON
                ),
                accessorKey: 'leadingCompliancePersonIndex',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    padding: '0px 0px 0px 16px',
                },
                cell: ({ getValue }) => (
                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        gap={0}
                        name={`corporateRoleFillings.${getValue<number | undefined>()!}.roleFilledByEmployee`}
                        paginated={{
                            fetch: useLazyGetEmployeesListWithFiltersQuery,
                            searchField: 'fullName',
                        }}
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        readonly={isReadOnly ? { linkTo: 'employee' } : false}
                        noReadOnlyDivider
                    />
                ),
            },
        ],
        [classifiers, control, setValue, isReadOnly]
    );

    return (
        <DataTable<CorporateRoleFillingRowType>
            columns={columns}
            data={dataToDisplay}
            columnConfigId="corporate-role-fillings"
        />
    );
};

export default CorporateRoleFillingsTable;
